import { Button, Typography } from '@design-system';
import React from 'react';
import './PointageItem.scss';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { GrFormCheckmark } from 'react-icons/gr';
import { RiHourglassFill } from 'react-icons/ri';
import { RxCross2 } from 'react-icons/rx';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { TaskState, Day } from '@services';

export type PointageItemProps = {
    dayId: string;
    day: string;
    date: Date;
    state: TaskState;
    holidayLabel: string | null;
    holiday: Day['holiday'];
    timeSpent?: number;
    optional?: boolean;
    canPointage: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const PointageItem: React.FC<PointageItemProps> = ({
    dayId,
    day,
    date,
    optional,
    timeSpent,
    state,
    holidayLabel,
    holiday,
    className,
    canPointage,
    ...props
}) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const weekIndex = searchParams.get('week') || '0';

    const handleOnClick = () => {
        if (
            holidayLabel ||
            (!optional &&
                !timeSpent &&
                state !== TaskState.VALIDATED &&
                state !== TaskState.WAITING &&
                state !== TaskState.REFUSED &&
                state !== TaskState.SYNC)
        )
            return;
        navigate(`/richesses-humaines/pointage/${dayId}?week=${weekIndex}`);
    };

    const hasWaitingTask = state === TaskState.WAITING;
    const hasRefusedTask = state === TaskState.REFUSED;
    const isValidated = state === TaskState.VALIDATED || state === TaskState.SYNC;

    return (
        <div className={`pointage-item ${className ?? ''}`} {...props} data-optional={optional} onClick={() => handleOnClick()}>
            <div className="pointage-item-content">
                <Typography intrinsic="h3" color={optional ? 'grey' : 'marine'}>
                    {day}
                </Typography>
                <Typography intrinsic="h2" variant="sub">
                    {date.toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit'
                    })}
                </Typography>
            </div>

            {!timeSpent && !hasWaitingTask && !hasRefusedTask && !isValidated && !holidayLabel && !optional && (
                <Button
                    color="green"
                    variant="secondary"
                    size="small"
                    onClick={() => navigate(`/richesses-humaines/pointage/${dayId}?week=${weekIndex}`)}
                    disabled={!canPointage}
                    icon={<MdKeyboardArrowRight />}
                    iconSize={24}
                    iconPosition="right"
                >
                    <Typography intrinsic="h3">Pointer</Typography>
                </Button>
            )}

            {hasWaitingTask && timeSpent && !holidayLabel && (
                <div className="pending-container">
                    <Typography intrinsic="h2" variant="sub">
                        {timeSpent}h
                    </Typography>
                    <div className="pointage-item-state pointage-item-state--pending">
                        <RiHourglassFill fontSize={26} color="#B3E0CA" />
                    </div>
                </div>
            )}

            {hasRefusedTask && timeSpent && !holidayLabel && (
                <div className="refused-container">
                    <Typography intrinsic="h2" variant="sub">
                        {timeSpent}h
                    </Typography>
                    <RxCross2 fontSize={30} color="white" />
                </div>
            )}

            {isValidated && timeSpent && !holidayLabel && (
                <div className="validated-container">
                    <Typography intrinsic="h2" variant="sub">
                        {timeSpent}h
                    </Typography>
                    <div className="pointage-item-state pointage-item-state--validated">
                        <GrFormCheckmark fontSize={26} color="white" />
                    </div>
                </div>
            )}

            {holidayLabel && (
                <div className="validated-container">
                    <Typography intrinsic="h2" variant="sub">
                        {holidayLabel}
                    </Typography>
                    <div
                        className={`pointage-item-state ${holiday.state_id == '2' ? 'pointage-item-state--validated' : 'pointage-item-state--pending'}`}
                    >
                        {holiday.state_id == '2' ? (
                            <GrFormCheckmark fontSize={26} color="white" />
                        ) : (
                            <RiHourglassFill fontSize={20} color="white" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
